import { type MessageSequenceStatus } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators.ts'
import { $expiringFansMessageSortOptions } from '@/modules/expiring-fans/models.ts'
import { useExpiringFanService } from '@/modules/expiring-fans/service/expiring-fans-fetch-context'
import type {
  ExpiringFanSettingsForm,
  ListExpiringFansResponseType,
} from '@/modules/expiring-fans/service/expiring-fans-service'
import type {
  ExpiringFansMessage,
  ExpiringFansMessageRequestBody,
} from '@/modules/expiring-fans/types.ts'
import { useExpiringFansFilter } from '@/modules/expiring-fans/use-expiring-fans-filter.tsx'
import { getErrorMessage, handleError } from '@/utils/error'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'
import { getSortDirection } from '@/utils/pagination/pagination-utils.ts'

const DEFAULT_SORT = 'totalSpend'

export function useExpiringFans() {
  const creatorId = useUnit($creatorId)

  const { list } = useExpiringFanService()
  const { pagination, searchQuery, sorting } = usePagination()
  const { filter } = useExpiringFansFilter()

  const [sort] = sorting
  const sortField = sort?.id ?? DEFAULT_SORT
  const sortDirection = getSortDirection(sort)

  return useQuery(
    ['expiring-fans', creatorId, pagination, searchQuery, filter, sorting],
    () =>
      list(creatorId!, searchQuery, filter, sortField, sortDirection, {
        size: pagination.pageSize,
        page: pagination.pageIndex,
      }),
    {
      enabled: !!creatorId,
      keepPreviousData: true,
    },
  )
}

export function useMarkAsMessaged() {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const { markAsMessaged } = useExpiringFanService()
  const { pagination, searchQuery, sorting } = usePagination()
  const { filter } = useExpiringFansFilter()

  return useMutation(
    ({ fanId, messaged }: { fanId: number; messaged: boolean }) => {
      if (!creatorId) {
        throw new Error('No creator selected')
      }

      return markAsMessaged(creatorId, fanId, messaged)
    },
    {
      onMutate: ({ fanId, messaged }) => {
        client.setQueryData<ListExpiringFansResponseType | undefined>(
          [
            'expiring-fans',
            creatorId,
            pagination,
            searchQuery,
            filter,
            sorting,
          ],
          (current) => {
            if (!current) {
              return current
            }

            return {
              ...current,
              content: current.content.map((fan) => {
                if (fan.fan.id === fanId) {
                  return {
                    ...fan,
                    status: messaged ? 'MANUALLY_MESSAGED' : 'NOT_MESSAGED',
                  }
                }

                return fan
              }),
            }
          },
        )
      },
      onSettled: () =>
        client.invalidateQueries(['expiring-fans', creatorId, pagination]),
    },
  )
}

export function useUpdateExpiringFanSettings() {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { updateSettings } = useExpiringFanService()

  return useMutation(
    (settings: ExpiringFanSettingsForm) => {
      if (!creatorId) {
        throw new Error('No creator selected')
      }

      return updateSettings(creatorId, settings)
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['expiringFansMessagesSettings', creatorId])
      },
      onError: () =>
        client.invalidateQueries(['expiringFansMessagesSettings', creatorId]),
    },
  )
}

export function useExpiringFansMessages() {
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const creatorId = useUnit($creatorId)

  const { getListMessages } = useExpiringFanService()

  return useQuery(
    ['expiringFansMessages', creatorId, sortOption],
    () => getListMessages(creatorId!, sortOption),
    {
      enabled: !!creatorId,
    },
  )
}

export function useGetExpiringFansMessageById(messageId: string | undefined) {
  const creatorId = useUnit($creatorId)

  const { getMessageById } = useExpiringFanService()

  return useQuery(
    ['expiringFansMessageById', creatorId, messageId],
    () => {
      if (!messageId) {
        return null
      }

      return getMessageById(creatorId!, messageId)
    },
    {
      enabled: !!creatorId,
    },
  )
}

export function useExpiringFansSettings() {
  const creatorId = useUnit($creatorId)

  const { getSettings } = useExpiringFanService()

  return useQuery(
    ['expiringFansMessagesSettings', creatorId],
    () => getSettings(creatorId!),
    {
      enabled: !!creatorId,
    },
  )
}

export function useCreateExpiringFansMessageMutation() {
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const { createMessage } = useExpiringFanService()
  const toast = useToast()

  return useMutation(
    (message: ExpiringFansMessageRequestBody) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return createMessage(creatorId, message)
    },
    {
      onError: (error) => {
        const detail = getErrorMessage(error)
        const text = detail?.toLowerCase().includes('timeframe')
          ? detail
          : 'Failed to create the message.'

        handleError(text)
      },
      onSuccess: () => {
        toast.success('Successfully created')
      },
      onSettled: () => {
        client.invalidateQueries([
          'expiringFansMessages',
          creatorId,
          sortOption,
        ])
      },
    },
  )
}

export function useDeleteExpiringFansMessageMutation() {
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const { deleteMessage } = useExpiringFanService()
  const toast = useToast()

  return useMutation(
    (messageId: number) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return deleteMessage(creatorId, messageId)
    },
    {
      onSuccess: () => {
        toast.success('Successfully deleted')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while deleting the message')
      },
      onSettled: () => {
        client.invalidateQueries([
          'expiringFansMessages',
          creatorId,
          sortOption,
        ])
      },
    },
  )
}

export function useUpdateExpiringFansMessageMutation() {
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { updateMessage } = useExpiringFanService()
  const toast = useToast()

  return useMutation(
    (message: ExpiringFansMessageRequestBody) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return updateMessage(creatorId, message)
    },
    {
      onError: (error) => {
        const detail = getErrorMessage(error)
        const text = detail?.toLowerCase().includes('timeframe')
          ? detail
          : 'Failed to create the message.'

        handleError(text)
      },
      onSuccess: () => {
        toast.success('Successfully updated')
      },
      onSettled: () => {
        client.invalidateQueries([
          'expiringFansMessages',
          creatorId,
          sortOption,
        ])
      },
    },
  )
}

export function useToggleExpiringFansMessageByMessagId() {
  const sortOption = useUnit($expiringFansMessageSortOptions)
  const toast = useToast()
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { updateExpiringFansMessageToggle } = useExpiringFanService()

  return useMutation(
    ({
      messageId,
      value,
    }: {
      messageId: string
      value: MessageSequenceStatus
    }) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return updateExpiringFansMessageToggle(creatorId, messageId, value)
    },
    {
      onMutate: (message) => {
        client.setQueryData<ExpiringFansMessage[] | undefined>(
          ['expiringFansMessages', creatorId, sortOption],
          (state) => {
            if (!state) {
              return state
            }

            return state.map((m) =>
              String(m.id) === message.messageId
                ? {
                    ...m,
                    sequence: {
                      ...m.sequence,
                      status: message.value,
                    },
                  }
                : m,
            )
          },
        )
      },
      onSuccess: () => {
        toast.success('Successfully updated')
      },
      onError: (error) => {
        handleError(error, 'Failed to update the message')
        client.invalidateQueries([
          'expiringFansMessages',
          creatorId,
          sortOption,
        ])
      },
    },
  )
}
